/* eslint-disable */
/**
 * Stateless utility functions for Google Maps operations.
 * Coordinates are in GeoJSON [lng, lat] format throughout.
 */
let _loadingPromise = null;
/**
 * Dynamically loads the Google Maps JavaScript API script.
 * Resolves immediately if the API is already loaded.
 * Deduplicates concurrent calls — only one script element is injected.
 */
export function loadGoogleMapsScript(apiKey, libraries) {
    if (typeof window === 'undefined')
        return Promise.resolve();
    if (window.google?.maps?.Map)
        return Promise.resolve();
    if (_loadingPromise)
        return _loadingPromise;
    _loadingPromise = new Promise((resolve, reject) => {
        const callbackName = '__pondlogsGmapsReady';
        window[callbackName] = () => {
            _loadingPromise = null;
            resolve();
        };
        const script = document.createElement('script');
        const libsParam = libraries.join(',');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=${libsParam}&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
            _loadingPromise = null;
            reject(new Error('Failed to load Google Maps script'));
        };
        document.head.appendChild(script);
    });
    return _loadingPromise;
}
/**
 * Creates a google.maps.Data.Feature from a GeoJSON coordinate ring.
 * @param coords  Outer ring as [lng, lat][] pairs (GeoJSON polygon ring)
 * @param id      Feature identifier (pondId)
 * @param props   Additional properties to attach to the feature
 */
export function getFeaturePolygon(coords, id, props) {
    const latLngs = coords.map(([lng, lat]) => new google.maps.LatLng(lat, lng));
    const geometry = new google.maps.Data.Polygon([latLngs]);
    const bounds = new google.maps.LatLngBounds();
    latLngs.forEach((ll) => bounds.extend(ll));
    // Compute a stable centroid for the feature
    const centre = getPolyCenter(coords);
    const propsCopy = {
        ...props,
        center: centre,
        // Keep both keys to stay compatible with legacy + current callsites.
        bounds,
        latLgnBounds: bounds,
    };
    return new google.maps.Data.Feature({ geometry, id, properties: propsCopy });
}
/**
 * Calculates the bounding-box centroid of a polygon given as [lng, lat][] pairs.
 */
export function getPolyCenter(coords) {
    const lats = coords.map(([, lat]) => lat).sort((a, b) => a - b);
    const lngs = coords.map(([lng]) => lng).sort((a, b) => a - b);
    const centerLat = lats[0] + (lats[lats.length - 1] - lats[0]) / 2;
    const centerLng = lngs[0] + (lngs[lngs.length - 1] - lngs[0]) / 2;
    return new google.maps.LatLng(centerLat, centerLng);
}
/**
 * Wraps a plain {lat, lng} literal in a google.maps.LatLng instance.
 */
export function getLatLng(obj) {
    return new google.maps.LatLng(obj.lat, obj.lng);
}
/**
 * Requests the user's current geolocation.
 * Rejects with an Error if geolocation is unsupported or denied.
 */
export function captureUserLocation() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by your browser'));
            return;
        }
        navigator.geolocation.getCurrentPosition((pos) => resolve(pos), (err) => reject(new Error(err.message)), { timeout: 10000 });
    });
}
